import { useEffect, useState } from "preact/hooks";
import { api } from "../api";
import { Sparkline } from "../components/Sparkline";
import type { BodyPoint, BodyTrends } from "@hh/shared";

const kgToLb = (kg: number) => Math.round(kg * 2.2046 * 10) / 10;

function Trend({ title, points, color, unit, latest, delta }: {
  title: string; points: BodyPoint[]; color: string; unit: string;
  latest: number | null; delta?: number | null;
}) {
  const deltaCls = delta == null || delta === 0 ? "flat" : delta < 0 ? "down" : "up";
  return (
    <div class="card">
      <div class="trend-head">
        <div>
          <h2 style={{ marginBottom: 2 }}>{title}</h2>
          <span class="now">{latest != null ? latest : "—"}<span class="unit"> {unit}</span></span>
        </div>
        {delta != null && (
          <span class={`delta ${deltaCls}`}>
            {delta > 0 ? "+" : ""}{delta} {unit} <span class="muted">/ 7d</span>
          </span>
        )}
      </div>
      <Sparkline points={points} color={color} />
    </div>
  );
}

function fmtSleep(min: number | null): string {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  return `${h}h ${min % 60}m`;
}

export function Body({ reloadKey }: { reloadKey: number }) {
  const [b, setB] = useState<BodyTrends | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true);
    api.body().then((d) => on && (setB(d), setLoading(false))).catch(() => on && setLoading(false));
    return () => { on = false; };
  }, [reloadKey]);

  const hasAny = b && (b.weight.length || b.bodyFat.length || b.muscle.length || b.latest.steps != null);

  return (
    <div class="app-shell fade-in">
      <div class="screen-head"><div><div class="eyebrow">Trends</div><h1>Body</h1></div></div>

      {!hasAny ? (
        <div class="card">
          <div class="empty">
            <div class="ico">📈</div>
            <p style={{ color: "var(--text-dim)", fontWeight: 600 }}>{loading ? "Loading…" : "No body data yet"}</p>
            <p>Weight, body-fat and muscle sync from Apple Health via your daily Shortcut. Set it up in Settings → Health (see SETUP).</p>
          </div>
        </div>
      ) : (
        <>
          <Trend title="Weight" points={b!.weight} color="var(--accent)" unit="kg"
            latest={b!.latest.weight} delta={b!.weightDelta7} />

          {b!.bodyFat.length > 0 && (
            <Trend title="Body fat" points={b!.bodyFat} color="var(--carbs)" unit="%"
              latest={b!.latest.body_fat_pct} />
          )}
          {b!.muscle.length > 0 && (
            <Trend title="Muscle mass" points={b!.muscle} color="var(--good)" unit="kg"
              latest={b!.latest.muscle_mass} />
          )}

          <div class="card">
            <h2>Latest from Health</h2>
            <div class="stat-grid">
              <div class="stat"><div class="v">{b!.latest.steps ?? "—"}</div><div class="l">steps</div></div>
              <div class="stat"><div class="v">{fmtSleep(b!.latest.sleep_minutes)}</div><div class="l">sleep</div></div>
              <div class="stat"><div class="v">{b!.latest.resting_hr ?? "—"}</div><div class="l">resting HR</div></div>
              <div class="stat"><div class="v">{b!.latest.hrv ?? "—"}<span class="unit">{b!.latest.hrv != null ? " ms" : ""}</span></div><div class="l">HRV</div></div>
            </div>
            {b!.latest.weight != null && (
              <p class="muted" style={{ marginTop: 12 }}>{kgToLb(b!.latest.weight)} lb · last updated {b!.latest.date}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
